import { c as _c } from "react/compiler-runtime";
import { ReactNode, useEffect, useId, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { RiCloseLine } from "react-icons/ri";
import { ShipmentPicker } from "@/components/inventory/shipment-picker";
import { LotShipmentDetails } from "@/components/inventory/shipment-picker/lot-shipment-details";
import Button from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { LotRowLabel } from "@/components/ui/lot-number-select";
import { useTenant } from "@/contexts/tenant-context";
import { useLocations } from "@/hooks/use-locations";
import { formatQuantity, formatUnit } from "@/lib/format-data";
import { DoseFormValues } from "./dose-form-schema";
import { DoseInventoryActions } from "./dose-inventory-actions";
import { DoseLotPickerDialog } from "./dose-lot-picker-content";
import { DoseLotPickerProvider } from "./dose-lot-picker-context";
import { DoseLotSelectionAction, getDoseDisplayedAllocations, getDoseInitialAllocations, getDoseLotCapabilities, getDosePickerShipments, getDoseReceiptIds, getRecordedDoseLots } from "./dose-lot-picker-model";
import { parseDoseLotReferences } from "./dose-lot-reference-options";
import { doseAllocationDecisionKey, doseLotCapacity } from "./dose-lot-resolution";
import { DoseSingleLotPicker } from "./dose-single-lot-picker";
import { inferDoseStockAdministrationQuotas } from "./dose-stock-planning";
import { DoseComponentInventoryEntry } from "./use-dose-component-inventory";
import { useTenantStore } from "@/stores/tenant-store";
function SelectedLotRow(t0) {
  const $ = _c(8);
  const {
    label,
    children,
    onRemove
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="min-w-0 flex-1">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label || $[3] !== onRemove) {
    t2 = onRemove && <Button type="button" size="sm" variant="ghost" aria-label={`Remove lot ${label}`} onClick={onRemove}><RiCloseLine aria-hidden="true" className="h-4 w-4" /></Button>;
    $[2] = label;
    $[3] = onRemove;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className="flex items-start gap-3 border-b border-border px-2 py-3 last:border-b-0">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
export function DoseLotPicker(props) {
  const $ = _c(3);
  const {
    tenant
  } = useTenant();
  let t0;
  if ($[0] !== props || $[1] !== tenant?.multiLotDrawdownEnabled) {
    t0 = tenant?.multiLotDrawdownEnabled === true ? <><Label>Lot Number</Label><DoseMultiLotPicker {...props} /></> : <DoseSingleLotPicker {...props} />;
    $[0] = props;
    $[1] = tenant?.multiLotDrawdownEnabled;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
function DoseMultiLotPicker(t0) {
  const $ = _c(41);
  const {
    index,
    inventory
  } = t0;
  const {
    control,
    setValue,
    formState: t1
  } = useFormContext();
  const {
    errors
  } = t1;
  const t2 = `components.${index}`;
  let t3;
  if ($[0] !== control || $[1] !== t2) {
    t3 = {
      control,
      name: t2
    };
    $[0] = control;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const component = useWatch(t3);
  const tenant = useTenantStore(_temp);
  const {
    allLocationNameById
  } = useLocations();
  const descriptionId = useId();
  const [openScope, setOpenScope] = useState(null);
  let t4;
  if ($[3] !== component || $[4] !== inventory) {
    t4 = getDoseLotCapabilities(component, inventory);
    $[3] = component;
    $[4] = inventory;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    action
  } = t4;
  const drawsInventory = action === DoseLotSelectionAction.DRAW;
  const preview = inventory.preview;
  const displayed = getDoseDisplayedAllocations(component, inventory);
  const recorded = getRecordedDoseLots(component);
  const receiptIds = getDoseReceiptIds(component);
  const shipments = getDosePickerShipments(component, inventory);
  let t5;
  if ($[6] !== component || $[7] !== index || $[8] !== inventory || $[9] !== tenant) {
    t5 = JSON.stringify([tenant, index, component.id, component.medicationId, inventory.locationId, inventory.providerId, inventory.compoundMedicationId, component.amount, component.quantity, component.unit, component.dilutionId, component.correctActualInventoryUse, getDoseLotCapabilities(component, {
      ...inventory,
      previewError: false
    }).action]);
    $[6] = component;
    $[7] = index;
    $[8] = inventory;
    $[9] = tenant;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const scope = t5;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setOpenScope(null);
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== scope) {
    t7 = [scope];
    $[12] = scope;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  useEffect(t6, t7);
  const error = errors.components?.[index]?.lotSelection?.message;
  const references = parseDoseLotReferences(component.lotSelection?.value);
  const listedReceipts = receiptIds.filter(id => !displayed.some(allocation => allocation.shipmentId === id));
  const canRemoveInventory = !inventory.isCheckingInventory && (action === DoseLotSelectionAction.DRAW || action === DoseLotSelectionAction.RECORD);
  const removeAllocation = shipmentId => {
    if (action !== DoseLotSelectionAction.DRAW || !preview) {
      return;
    }
    const next = displayed.filter(row => row.shipmentId !== shipmentId);
    const quotas = inferDoseStockAdministrationQuotas(next, preview.stocks, component.quantity);
    const previousKey = doseAllocationDecisionKey(component, inventory);
    const key = doseAllocationDecisionKey({
      ...component,
      lotAllocations: next,
      stockAdministrationQuotas: quotas
    }, inventory);
    setValue(`components.${index}.lotAllocationBasis`, undefined, {
      shouldDirty: true
    });
    setValue(`components.${index}.lotAllocations`, next.map(row_0 => row_0.allowExtraYield && row_0.decisionKey === JSON.stringify([previousKey, row_0.quantity]) ? {
      ...row_0,
      decisionKey: JSON.stringify([key, row_0.quantity])
    } : row_0), {
      shouldDirty: true,
      shouldValidate: true
    });
    setValue(`components.${index}.stockAdministrationQuotas`, quotas, {
      shouldDirty: true,
      shouldValidate: true
    });
    setValue(`components.${index}.checkoutReuse`, false, {
      shouldDirty: true
    });
  };
  const lotActionLabel = references.length > 0 || listedReceipts.length > 0 || displayed.length > 0 ? "Update lot numbers" : "Choose lot number";
  const t8 = "space-y-2";
  const t9 = references.map(code => <SelectedLotRow key={code} label={code} onRemove={inventory.isCheckingInventory ? undefined : () => {
    const next_0 = references.filter(reference => reference !== code);
    setValue(`components.${index}.lotSelection`, next_0.length ? {
      type: "lot",
      value: next_0.join("\n")
    } : null, {
      shouldDirty: true,
      shouldValidate: true
    });
  }}><LotRowLabel lotNumber={code} productName={component.name} wrap={true} /></SelectedLotRow>);
  const t10 = listedReceipts.map(id_0 => {
    const lot = inventory.shipments.find(row_1 => row_1.id === id_0);
    const label = lot?.lotNumber || recorded.find(row_2 => row_2.shipmentId === id_0)?.lotNumber || "Unlabeled lot";
    return <SelectedLotRow key={id_0} label={label} onRemove={canRemoveInventory && action === DoseLotSelectionAction.RECORD ? () => {
      setValue(`components.${index}.clinicalShipmentIds`, receiptIds.filter(receiptId => receiptId !== id_0), {
        shouldDirty: true,
        shouldValidate: true
      });
    } : undefined}>{lot ? <LotShipmentDetails shipment={lot} productName={component.name} locationNames={allLocationNameById} /> : <LotRowLabel lotNumber={label} productName={component.name} wrap={true} />}</SelectedLotRow>;
  });
  const t11 = displayed.map(allocation_0 => {
    const lot_0 = inventory.shipments.find(candidate => candidate.id === allocation_0.shipmentId);
    const stockUnit = preview?.stocks.find(entry => entry.stockId === allocation_0.stockId)?.unit;
    const label_0 = lot_0?.lotNumber || recorded.find(entry_0 => entry_0.shipmentId === allocation_0.shipmentId)?.lotNumber || "Missing or unlabeled lot";
    return <SelectedLotRow key={allocation_0.shipmentId} label={label_0} onRemove={canRemoveInventory && drawsInventory ? () => removeAllocation(allocation_0.shipmentId) : undefined}>{lot_0 ? <LotShipmentDetails shipment={lot_0} productName={component.name} locationNames={allLocationNameById} /> : <LotRowLabel lotNumber={label_0} productName={component.name} wrap={true} />}<p className="mt-1 text-caption text-foreground-secondary">{component.inventoryBaseline?.automaticShipmentIds?.includes(allocation_0.shipmentId) && <span>Automatically selected · </span>}{`${formatQuantity(component.checkoutReuse ? lot_0?.chargedTodayQuantity ?? 0 : allocation_0.quantity)} ${stockUnit ? formatUnit(stockUnit) : ""}${component.checkoutReuse ? " already charged at checkout (not resized)" : ""}`}</p></SelectedLotRow>;
  });
  let t12;
  if ($[14] !== index || $[15] !== inventory) {
    t12 = <DoseInventoryActions index={index} inventory={inventory} />;
    $[14] = index;
    $[15] = inventory;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== scope) {
    t13 = () => setOpenScope(scope);
    $[17] = scope;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== descriptionId || $[20] !== inventory.isCheckingInventory || $[21] !== lotActionLabel || $[22] !== t13) {
    t14 = <Button aria-describedby={descriptionId} type="button" className="h-auto w-full max-w-full whitespace-normal text-left" disabled={inventory.isCheckingInventory} onClick={t13}>{lotActionLabel}</Button>;
    $[19] = descriptionId;
    $[20] = inventory.isCheckingInventory;
    $[21] = lotActionLabel;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const t15 = inventory.selectedMedication?.name ?? component.name;
  let t16;
  if ($[24] !== descriptionId || $[25] !== t15) {
    t16 = <span id={descriptionId} hidden={true}>{t15}</span>;
    $[24] = descriptionId;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== inventory.previewError) {
    t17 = inventory.previewError && <p role="alert" className="text-caption text-status-warning-text">Inventory requirements could not be verified. Refresh and try again.</p>;
    $[27] = inventory.previewError;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== error) {
    t18 = error && <p role="alert" className="text-caption text-status-warning-text">{String(error)}</p>;
    $[29] = error;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  const t19 = openScope === scope && <ShipmentPicker.Root shipments={shipments} initialAllocations={getDoseInitialAllocations(component, inventory, shipments)} unit={drawsInventory && preview?.stocks[0]?.unit || component.unit} stockName={inventory.selectedMedication?.name ?? component.name} shipmentConversions={drawsInventory && preview ? Object.fromEntries(shipments.map(lot_1 => {
    const stock = preview.stocks.find(entry_1 => entry_1.stockId === lot_1.stockId);
    return [lot_1.id, {
      factor: stock.perAdministration,
      unit: stock.unit
    }];
  })) : undefined} enableExtraYield={drawsInventory} decisionKey={doseAllocationDecisionKey(component, inventory)} capacities={Object.fromEntries(inventory.shipments.map(lot_2 => [lot_2.id, doseLotCapacity(lot_2, inventory)]))} initialShowDepleted={component.includeDepletedMedicationId === component.medicationId} onShowDepletedChange={show => setValue(`components.${index}.includeDepletedMedicationId`, show ? component.medicationId : undefined)}><DoseLotPickerProvider index={index} component={component} inventory={inventory} close={() => setOpenScope(null)}><DoseLotPickerDialog title={lotActionLabel} /></DoseLotPickerProvider></ShipmentPicker.Root>;
  let t20;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t12 || $[34] !== t14 || $[35] !== t16 || $[36] !== t17 || $[37] !== t18 || $[38] !== t19 || $[39] !== t9) {
    t20 = <div className={t8}>{t9}{t10}{t11}{t12}{t14}{t16}{t17}{t18}{t19}</div>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t14;
    $[35] = t16;
    $[36] = t17;
    $[37] = t18;
    $[38] = t19;
    $[39] = t9;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  return t20;
}
function _temp(state) {
  return state.currentTenant;
}